import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useSession } from "../../features/auth/hooks";
import { Avatar } from "../ui/Avatar";

export function DesktopTopBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSession();
  // Mirror the canonical ?tag filter so the bar always shows the active search —
  // on reload, browser back/forward, or an edit from the in-route TagSearchBox —
  // instead of going blank while the list below stays filtered. Sync via the
  // "adjust state during render" pattern (not an effect): when the URL filter
  // changes out from under the local draft, snap the draft to it.
  const activeTag = searchParams.get("tag") ?? "";
  const [query, setQuery] = useState(activeTag);
  const [syncedTag, setSyncedTag] = useState(activeTag);
  if (activeTag !== syncedTag) {
    setSyncedTag(activeTag);
    setQuery(activeTag);
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    // Target /chargers explicitly — that route reads ?tag and drives both the
    // list and the map markers. URLSearchParams matches TagSearchBox's encoding
    // (spaces as `+`), so both entry points yield one canonical URL per query.
    const params = new URLSearchParams({ tag: q });
    navigate(`/chargers?${params.toString()}`);
  }

  function clearSearch() {
    setQuery("");
    navigate("/chargers");
  }

  return (
    <div className="flex h-full items-center justify-between gap-4 px-6">
      <form role="search" onSubmit={handleSearchSubmit} className="relative max-w-xl flex-1">
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="m17 17-3.5-3.5" />
        </svg>
        <input
          type="search"
          name="topbar-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("nav.search_placeholder")}
          aria-label={t("nav.search_placeholder")}
          className="w-full rounded-full border border-line bg-surface py-2 pl-12 pr-10 text-sm text-fg shadow-sm transition-colors placeholder:text-fg-faint focus:border-volt focus:outline-none focus:ring-2 focus:ring-volt/30"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={clearSearch}
            aria-label={t("nav.search_clear")}
            className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-fg-faint transition-colors hover:bg-surface-raised hover:text-fg"
          >
            <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="m5 5 10 10M15 5 5 15" />
            </svg>
          </button>
        )}
      </form>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={t("nav.notifications")}
          className="flex h-10 w-10 items-center justify-center rounded-md text-fg-mute hover:text-fg"
        >
          <BellGlyph />
        </button>
        <Link
          to="/settings"
          aria-label={t("nav.settings")}
          className="flex h-10 w-10 items-center justify-center rounded-md text-fg-mute hover:text-fg"
        >
          <CogGlyph />
        </Link>
        <Link to="/profile" aria-label={t("nav.tab_profile")}>
          <Avatar userId={user?.id ?? null} label={user?.email ?? null} size={32} />
        </Link>
      </div>
    </div>
  );
}

function BellGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 3a4 4 0 0 0-4 4c0 4-1.5 5.5-2 6h12c-.5-.5-2-2-2-6a4 4 0 0 0-4-4Z" />
      <path d="M8.5 16a1.6 1.6 0 0 0 3 0" />
    </svg>
  );
}

function CogGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4" />
    </svg>
  );
}
